import type {
  AnalyticsEventName,
  AnalyticsEventProperties,
  AnalyticsHost,
  QuerySkillsAnalyticsProperties,
  AnalyticsSurface,
} from "./events";
import { AnalyticsEvents } from "./events";
import { trackAnalyticsEvent } from "./client";
import { ANALYTICS_APP_SOURCE } from "./context";
import type {
  AnalyticsContext,
  BuildQuerySkillsAnalyticsParams,
  TrackDeckCreationCompletedParams,
  TrackDeckCreationStartedParams,
  TrackDeckReviewCompletedParams,
  TrackFeedbackDetailsSubmittedParams,
  TrackFeedbackRatingUpdatedParams,
  TrackFeedbackSubmittedParams,
  TrackProperties,
  TrackQueryRunParams,
  TrackQuerySubmittedParams,
} from "./product.types";

export { AnalyticsEvents } from "./events";
export type {
  BuildQuerySkillsAnalyticsParams,
  TrackDeckCreationCompletedParams,
  TrackDeckCreationStartedParams,
  TrackDeckReviewCompletedParams,
  TrackFeedbackDetailsSubmittedParams,
  TrackFeedbackRatingUpdatedParams,
  TrackFeedbackSubmittedParams,
  TrackProperties,
  TrackQueryRunParams,
  TrackQuerySubmittedParams,
} from "./product.types";
export type { QuerySkillsAnalyticsProperties } from "./events";

/**
 * Product-facing analytics API.
 *
 * Feature code should import these named helpers instead of importing
 * `client.ts` or Amplitude directly. Event property names are camelCase,
 * matching Finster Chat.
 */
const TASK_PANE_SURFACE: AnalyticsSurface = "task_pane";
const TASKPANE_SESSION_STARTED_PREFIX = "finsterAnalytics.taskpaneSessionStarted";

function getAnalyticsHost(): AnalyticsHost | null {
  const hostInfo = window.__FINSTER_HOST_INFO__;
  if (!hostInfo) return null;
  return hostInfo.isExcel ? "excel" : "powerpoint";
}

function getAnalyticsContext(
  surface: AnalyticsSurface = TASK_PANE_SURFACE
): AnalyticsContext | null {
  const host = getAnalyticsHost();
  if (!host) return null;

  return {
    appSource: ANALYTICS_APP_SOURCE,
    host,
    platform: window.__FINSTER_HOST_INFO__?.platform ?? "unknown",
    surface,
  };
}

/** Events that exist once per Office host; add a pair here to gain both. */
const HOST_EVENTS = {
  taskpaneSessionStarted: {
    powerpoint: AnalyticsEvents.PptTaskpaneSessionStarted,
    excel: AnalyticsEvents.ExcelTaskpaneSessionStarted,
  },
  querySubmitted: {
    powerpoint: AnalyticsEvents.PptQuerySubmitted,
    excel: AnalyticsEvents.ExcelQuerySubmitted,
  },
  queryRun: {
    powerpoint: AnalyticsEvents.PptQueryRun,
    excel: AnalyticsEvents.ExcelQueryRun,
  },
} as const satisfies Record<string, Record<AnalyticsHost, AnalyticsEventName>>;

/**
 * Drops properties the caller left unset.
 *
 * Empty strings are dropped alongside `undefined` because the server rejects
 * an allowlisted property whose value is empty, and a rejected event is
 * discarded by the client rather than retried.
 */
function withSetPropertiesOnly<T extends Record<string, unknown>>(properties: T): T {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined && value !== "")
  ) as T;
}

const SKILL_SELECTION_PATHS = new Set(["explicit", "inferred", "none"]);
const SKILL_RESOLUTION_STATUSES = new Set([
  "cleared",
  "inherited",
  "resolved",
  "none",
  "fail-open",
  "blocked",
  "aborted",
]);
const SKILL_ANALYTICS_ORIGINS = ["personal", "organization", "finster"] as const;

function readNonNegativeInteger(value: number | undefined): number | undefined {
  return Number.isSafeInteger(value) && Number(value) >= 0 ? Number(value) : undefined;
}

function readNonNegativeDuration(value: number | undefined): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : undefined;
}

function readAnalyticsToken(value: string | null | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized && /^[A-Za-z0-9._:-]{1,64}$/.test(normalized) ? normalized : undefined;
}

function summarizeSkillOrigins(
  origins: BuildQuerySkillsAnalyticsParams["origins"]
): string | undefined {
  if (!origins?.length) return undefined;
  const counts = new Map<string, number>();
  for (const origin of origins) {
    if (!SKILL_ANALYTICS_ORIGINS.includes(origin)) continue;
    counts.set(origin, (counts.get(origin) ?? 0) + 1);
  }
  const summary = SKILL_ANALYTICS_ORIGINS.flatMap((origin) => {
    const count = counts.get(origin);
    return count ? [`${origin}:${count}`] : [];
  }).join(",");
  return summary || undefined;
}

/**
 * Builds privacy-safe Skill metadata for host-specific Langfuse observations.
 * It is not sent to Amplitude.
 *
 * `enabled=false` returns no metadata, so flag-off runs cannot accidentally
 * emit a Skills payload. The input deliberately accepts only summaries,
 * counts, booleans, and timings—not raw guidance.
 */
export function buildQuerySkillsAnalytics(
  params: BuildQuerySkillsAnalyticsParams
): QuerySkillsAnalyticsProperties | undefined {
  if (
    !params.enabled ||
    !SKILL_SELECTION_PATHS.has(params.selectionPath) ||
    !SKILL_RESOLUTION_STATUSES.has(params.status)
  ) {
    return undefined;
  }

  const originSummary = summarizeSkillOrigins(params.origins);
  const selectorPath = readAnalyticsToken(params.selectorPath);
  const selectorSource = readAnalyticsToken(params.selectorSource);

  return withSetPropertiesOnly({
    skillSelectionPath: params.selectionPath,
    skillResolutionStatus: params.status,
    skillCount:
      readNonNegativeInteger(params.skillCount) ??
      readNonNegativeInteger(params.origins?.length) ??
      0,
    skillOriginSummary: originSummary,
    skillSelectorPath: selectorPath,
    skillSelectorSource: selectorSource,
    skillPolicyVersion: readNonNegativeInteger(params.policyVersion),
    skillResolveLatencyMs: readNonNegativeDuration(params.resolveLatencyMs),
    skillSelectorLatencyMs: readNonNegativeDuration(params.selectorLatencyMs),
    skillSelectorFallback: params.fallback,
    skillTargetsStripped: params.targetsStripped,
    skillEffectiveSourceCount: readNonNegativeInteger(params.effectiveSourceCount),
    skillEffectiveFileCount: readNonNegativeInteger(params.effectiveFileCount),
    skillGuidanceChars: readNonNegativeInteger(params.guidanceChars),
    skillPlanningEvidenceCount: readNonNegativeInteger(params.planningEvidenceCount),
    skillPlanningEvidenceCitationCount: readNonNegativeInteger(
      params.planningEvidenceCitationCount
    ),
    skillPlanningEvidenceChars: readNonNegativeInteger(params.planningEvidenceChars),
    skillPlanningEvidenceSizeBytes: readNonNegativeInteger(params.planningEvidenceSizeBytes),
    skillPlanningEvidenceTruncated: params.planningEvidenceTruncated,
  });
}

/**
 * Runs a tracking call once host context is resolvable.
 *
 * A missing host means the event is dropped before it ever reaches the outbox,
 * which is the one loss the sequence numbers cannot detect. It is logged so the
 * gap is attributable rather than invisible.
 */
function withAnalyticsContext(
  track: (context: AnalyticsContext) => void,
  surface: AnalyticsSurface = TASK_PANE_SURFACE
): void {
  const context = getAnalyticsContext(surface);
  if (!context) {
    console.warn("[Analytics] Event dropped: Office host info is not available yet", { surface });
    return;
  }
  track(context);
}

export function track<E extends AnalyticsEventName>(
  event: E,
  properties: TrackProperties<E>,
  surface: AnalyticsSurface = TASK_PANE_SURFACE
): void {
  withAnalyticsContext((context) => {
    trackAnalyticsEvent(event, {
      ...context,
      ...properties,
    } as AnalyticsEventProperties[E]);
  }, surface);
}

let taskpaneSessionStartedTracked = false;

export function trackTaskpaneSessionStarted(): void {
  if (taskpaneSessionStartedTracked) return;

  withAnalyticsContext((context) => {
    const sessionKey = `${TASKPANE_SESSION_STARTED_PREFIX}.${context.host}.${context.surface}`;
    try {
      if (window.sessionStorage.getItem(sessionKey) === "1") {
        taskpaneSessionStartedTracked = true;
        return;
      }
      window.sessionStorage.setItem(sessionKey, "1");
    } catch {
      // If sessionStorage is unavailable, fall back to in-memory de-dupe.
    }

    taskpaneSessionStartedTracked = true;
    trackAnalyticsEvent(HOST_EVENTS.taskpaneSessionStarted[context.host], context);
  });
}

export function trackQuerySubmitted(params: TrackQuerySubmittedParams): void {
  withAnalyticsContext((context) => {
    trackAnalyticsEvent(HOST_EVENTS.querySubmitted[context.host], {
      ...context,
      mode: params.mode,
      hasContext: params.hasContext,
    });
  }, params.surface);
}

export function trackQueryRun(params: TrackQueryRunParams): void {
  withAnalyticsContext((context) => {
    trackAnalyticsEvent(
      HOST_EVENTS.queryRun[context.host],
      withSetPropertiesOnly({
        ...context,
        mode: params.mode,
        hasContext: params.hasContext,
        durationMs: Math.max(0, Math.round(params.durationMs)),
        status: params.status,
        errorType: params.errorType,
        needsClarification: params.needsClarification,
        workbookEditAttempted: params.workbookEditAttempted,
        workbookEditSuccess: params.workbookEditSuccess,
        operationCount: params.operationCount,
        affectedSheetCount: params.affectedSheetCount,
      })
    );
  }, params.surface);
}

export function trackDeckCreationStarted(params: TrackDeckCreationStartedParams): void {
  track(
    AnalyticsEvents.DeckCreationStarted,
    {
      requestedCount: params.requestedCount,
      brandStyle: params.brandStyle,
      hasReferenceDeck: params.hasReferenceDeck,
      taskOutputUsed: params.taskOutputUsed,
    },
    params.surface
  );
}

export function trackDeckCreationCompleted(params: TrackDeckCreationCompletedParams): void {
  track(
    AnalyticsEvents.DeckCreationCompleted,
    {
      requestedCount: params.requestedCount,
      finalCount: params.finalCount,
      successCount: params.successCount,
      durationMs: Math.max(0, Math.round(params.durationMs)),
      status: params.status,
      brandStyle: params.brandStyle,
      taskOutputUsed: params.taskOutputUsed,
    },
    params.surface
  );
}

export function trackDeckReviewCompleted(params: TrackDeckReviewCompletedParams): void {
  track(
    AnalyticsEvents.DeckReviewCompleted,
    withSetPropertiesOnly({
      requestedCount: params.requestedCount,
      durationMs: Math.max(0, Math.round(params.durationMs)),
      status: params.status,
      slidesReviewed: params.slidesReviewed,
      issuesFound: params.issuesFound,
      errorType: params.errorType,
    }),
    params.surface
  );
}

export function trackFeedbackSubmitted(params: TrackFeedbackSubmittedParams): void {
  track(
    AnalyticsEvents.FeedbackSubmitted,
    {
      feedbackType: params.feedbackType,
      source: params.source,
      success: params.success,
    },
    params.surface
  );
}

export function trackFeedbackRatingUpdated(params: TrackFeedbackRatingUpdatedParams): void {
  track(
    AnalyticsEvents.FeedbackRatingUpdated,
    withSetPropertiesOnly({
      action: params.action,
      value: params.value,
      outputId: params.outputId,
      outputType: params.outputType,
      area: params.area,
      sessionId: params.sessionId,
      queryId: params.queryId,
      artifactKind: params.artifactKind,
    }),
    params.surface
  );
}

export function trackFeedbackDetailsSubmitted(params: TrackFeedbackDetailsSubmittedParams): void {
  track(
    AnalyticsEvents.FeedbackDetailsSubmitted,
    withSetPropertiesOnly({
      value: params.value,
      outputId: params.outputId,
      outputType: params.outputType,
      area: params.area,
      sessionId: params.sessionId,
      queryId: params.queryId,
      artifactKind: params.artifactKind,
      reasons: params.reasons && params.reasons.length > 0 ? params.reasons : undefined,
    }),
    params.surface
  );
}
